import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import type { Modul, NavEintrag, Unterpunkt, Zaehlerwert } from "../modul";
import { GruppenKontext } from "../gruppen";
import {
  kapselKennzahlenAus,
  KapselKennzahlenKontext,
  kapselReiterAus,
  KapselReiterKontext,
  unterlageVorschauAus,
  UnterlageVorschauKontext,
} from "../kapsel";
import { navigationFuer, pfadGehoertZu, type NavPunkt } from "../navigation";
import { DIALOG_GEOEFFNET, dialogZiel, MITTEILUNGEN, useDialog } from "../dialog";
import { glockeText, MitteilungOrtKontext, MITTEILUNGEN_PFAD, useUngelesen } from "../mitteilungen";
import { useAngemeldet } from "../sitzung";
import { Glocke } from "../ui/Glocke";
import { Aufklappbar } from "./Aufklappbar";
import { mitReiterFokus } from "../ui/Reiter";
import { Icon } from "./Icon";
import { InstallHinweis } from "./InstallHinweis";
import { MitteilungenLeiste } from "./MitteilungenLeiste";
import { PersonenMenue } from "./PersonenMenue";
import { Wortmarke } from "../ui/Wortmarke";
import s from "./shell.module.css";

/** id der Glocke in der Kopfzeile: Dorthin geht der Fokus zurück, wenn die Leiste schließt. */
const GLOCKE = "glocke";

function Marke() {
  return (
    <NavLink to="/" className={s.marke} aria-label="obsn App, zur Startseite">
      <Wortmarke /> <small>App</small>
    </NavLink>
  );
}

/** Glocke in der Kopfzeile: öffnet die Mitteilungen als Leiste über der Seite (?mitteilungen=1). */
function GlockeKnopf({ ungelesen, suche }: { ungelesen: number; suche: string }) {
  const navigate = useNavigate();
  const { offen } = useDialog(MITTEILUNGEN);
  return (
    <button
      type="button"
      id={GLOCKE}
      className={s.glockeKnopf}
      aria-label={glockeText(ungelesen)}
      aria-haspopup="dialog"
      aria-expanded={offen}
      onClick={() => navigate(dialogZiel(MITTEILUNGEN, suche), { state: DIALOG_GEOEFFNET })}
    >
      <Glocke ungelesen={ungelesen} />
    </button>
  );
}

export function Shell({ module }: { module: readonly Modul[] }) {
  const sitzung = useAngemeldet();
  // Die Reiter der Gruppen gehen per Kontext an die Seiten: nur neu berechnen, wenn sich Module oder Rechte ändern
  const { bereiche, leiste, gruppen } = useMemo(() => navigationFuer(module, sitzung.rechte), [module, sitzung.rechte]);
  const ueberlagerungen = module.flatMap((m) => m.ueberlagerungen ?? []);
  const orte = useMemo(() => module.flatMap((m) => (m.mitteilungOrte ? [m.mitteilungOrte] : [])), [module]);
  const kapselReiter = useMemo(() => kapselReiterAus(module), [module]);
  const kapselKennzahlen = useMemo(() => kapselKennzahlenAus(module), [module]);
  const unterlageVorschau = useMemo(() => unterlageVorschauAus(module), [module]);
  const ungelesen = useUngelesen();
  const ort = useLocation();
  // „Mehr“ gilt nur für die Seite, auf der es geöffnet wurde: Nach einem Seitenwechsel ist es von selbst zu.
  const [mehrOffenAuf, setMehrOffenAuf] = useState<string | null>(null);
  const mehrOffen = mehrOffenAuf === ort.pathname;
  const setMehrOffen = (offen: boolean) => setMehrOffenAuf(offen ? ort.pathname : null);
  const haupt = useRef<HTMLElement>(null);

  // Nach jedem Seitenwechsel: Fokus auf den Inhalt (Tastatur und Screenreader) und nach oben.
  // Steht das Ziel eines Ankers schon da (/profil#push), hat die Seite selbst dorthin gescrollt: Dieser Effekt läuft nach denen der Seite.
  // Wechsel mit den Pfeiltasten zwischen Reitern (Sitzungen, Sprint): Der Fokus bleibt auf dem Reiter, den die Seite gesetzt hat.
  const reiterFokus = mitReiterFokus(ort.state);
  useEffect(() => {
    if (!reiterFokus) haupt.current?.focus({ preventScroll: true });
    const anker = window.location.hash.slice(1);
    if (!anker || !document.getElementById(anker)) window.scrollTo(0, 0);
    // Nur beim Seitenwechsel, nicht wenn sich allein der Zustand ändert
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ort.pathname]);

  // Mitteilungen und Profil sitzen in der Kopfzeile (KOPFZEILE.md, Abschnitt 3.2), „unten“ steht darum nicht in der Leiste. Unten über
  // einer Linie nur die Verwaltung (BAUPLAN-SEITENLEISTE.md, Abschnitt 6).
  const sichtbar = bereiche.filter((b) => b.id !== "unten");
  const navBereich = (b: (typeof bereiche)[number]) => {
    const liste = (
      <ul className={s.liste}>
        {b.eintraege.map((e) =>
          e.bedingung ? (
            <Bedingt key={e.pfad} quelle={e.bedingung}>
              <Eintrag e={e} jetzt={ort.pathname} />
            </Bedingt>
          ) : (
            <Eintrag key={e.pfad} e={e} jetzt={ort.pathname} />
          ),
        )}
      </ul>
    );
    if (b.aufklappbar && b.titel) {
      const aktivDrin = b.eintraege.some((e) => e.pfade.some((p) => pfadGehoertZu(ort.pathname, p)));
      return (
        <Aufklappbar key={b.id} id={b.id} titel={b.titel} anzahl={b.eintraege.length} aktivDrin={aktivDrin}>
          {liste}
        </Aufklappbar>
      );
    }
    return (
      <nav key={b.id} aria-label={b.name}>
        {b.titel ? <p className={s.titel}>{b.titel}</p> : null}
        {liste}
      </nav>
    );
  };
  const fuss = sichtbar.filter((b) => b.fuss);
  const navListe = (
    <>
      {sichtbar.filter((b) => !b.fuss).map(navBereich)}
      {fuss.length ? <div className={s.fuss}>{fuss.map(navBereich)}</div> : null}
    </>
  );

  return (
    <GruppenKontext.Provider value={gruppen}>
      <MitteilungOrtKontext.Provider value={orte}>
        <KapselReiterKontext.Provider value={kapselReiter}>
          <KapselKennzahlenKontext.Provider value={kapselKennzahlen}>
            <UnterlageVorschauKontext.Provider value={unterlageVorschau}>
              <div className={s.app}>
                <a href="#inhalt" className={s.springen}>
                  Zum Inhalt
                </a>
                <aside className={s.seite}>
                  <Marke />
                  {navListe}
                </aside>

                <div className={s.spalte}>
                  {/* Kopfzeile (KOPFZEILE.md, Abschnitt 3.1): ab 900 px schmal mit Glocke und Bild rechts, darunter wie bisher Marke links und
                Bild rechts. Die Glocke sitzt dort in der unteren Leiste. */}
                  <header className={s.kopf}>
                    <Marke />
                    <div className={s.kopfRechts}>
                      <GlockeKnopf ungelesen={ungelesen} suche={ort.search} />
                      <PersonenMenue />
                    </div>
                  </header>

                  <main id="inhalt" ref={haupt} tabIndex={-1} className={s.haupt}>
                    <InstallHinweis />
                    <Outlet />
                  </main>
                </div>

                <nav className={s.leiste} aria-label="Schnellzugriff">
                  {leiste.map((kandidaten) => (
                    <LeistenPlatz key={kandidaten.map((e) => e.pfad).join(" ")} kandidaten={kandidaten} suche={ort.search} ungelesen={ungelesen} />
                  ))}
                  <button type="button" onClick={() => setMehrOffen(true)} aria-expanded={mehrOffen} aria-controls="mehr">
                    <Icon name="mehr" groesse={22} />
                    Mehr
                  </button>
                </nav>

                <div id="mehr" className={s.mehr} data-offen={mehrOffen} hidden={!mehrOffen}>
                  <div className={s.mehrKopf}>
                    <Marke />
                    <button type="button" className={s.schliessen} onClick={() => setMehrOffen(false)}>
                      Schließen
                    </button>
                  </div>
                  {navListe}
                </div>

                {/* Vor den Überlagerungen der Module: Öffnet ein Klick in der Leiste die Aufgabe, schließt die Leiste zuerst. */}
                <MitteilungenLeiste glocke={GLOCKE} />
                {ueberlagerungen.map((Ueberlagerung, i) => (
                  <Ueberlagerung key={i} />
                ))}
              </div>
            </UnterlageVorschauKontext.Provider>
          </KapselKennzahlenKontext.Provider>
        </KapselReiterKontext.Provider>
      </MitteilungOrtKontext.Provider>
    </GruppenKontext.Provider>
  );
}

/** Eintrag der Seitenleiste als Listenpunkt, mit Unterpunkten, wenn das Modul welche liefert (die eigenen Vorhaben). */
function Eintrag({ e, jetzt }: { e: NavPunkt; jetzt: string }) {
  return <li>{e.unterpunkte ? <MitUnterpunkten e={e} quelle={e.unterpunkte} jetzt={jetzt} /> : <Punkt e={e} jetzt={jetzt} />}</li>;
}

/** Markiert auf der eigenen Seite und auf allen Seiten darunter. Eintrag einer Gruppe (Sitzungen, Aufgaben) ist aktiv, solange eine ihrer
 *  Seiten offen ist. `aktiv` setzt die Markierung von außen (Vorhaben neben seinen Unterpunkten). */
function Punkt({ e, jetzt, aktiv }: { e: NavPunkt; jetzt: string; aktiv?: boolean }) {
  const inhalt = (
    <>
      <Icon name={e.icon} />
      <span className={s.wort}>{e.label}</span>
      {e.zaehler ? <Zaehler quelle={e.zaehler} klasse={s.zahl} /> : null}
    </>
  );
  if (aktiv !== undefined || e.pfade.length > 1) {
    const an = aktiv ?? e.pfade.some((p) => pfadGehoertZu(jetzt, p));
    return (
      <Link to={e.pfad} className={s.link} aria-current={an ? "page" : undefined}>
        {inhalt}
      </Link>
    );
  }
  return (
    <NavLink to={e.pfad} end={e.pfad === "/"} className={s.link}>
      {inhalt}
    </NavLink>
  );
}

/** Eintrag mit Unterpunkten (BAUPLAN-SEITENLEISTE.md, Abschnitt 7): „Vorhaben“ ist auf /vorhaben markiert, eine Kapsel auf ihrer Seite,
 *  nie beide. Steht das geöffnete Vorhaben nicht darunter (nicht das eigene), bleibt „Vorhaben“ markiert. */
function MitUnterpunkten({ e, quelle, jetzt }: { e: NavPunkt; quelle: () => readonly Unterpunkt[]; jetzt: string }) {
  const punkte = quelle();
  const unterAktiv = punkte.some((u) => !u.alle && pfadGehoertZu(jetzt, u.pfad));
  return (
    <>
      <Punkt e={e} jetzt={jetzt} aktiv={pfadGehoertZu(jetzt, e.pfad) && !unterAktiv} />
      {punkte.length > 0 ? (
        <ul className={s.unterListe} aria-label={`Deine ${e.label}`}>
          {punkte.map((u) => (
            <li key={`${u.pfad}${u.alle ? "-alle" : ""}`}>
              {u.alle ? (
                <Link to={u.pfad} className={s.unterLink}>
                  <span className={s.wort}>{u.label}</span>
                </Link>
              ) : (
                <NavLink to={u.pfad} className={s.unterLink}>
                  <span className={s.punkt} style={u.farbe ? { background: u.farbe } : undefined} aria-hidden="true" />
                  <span className={s.wort}>{u.label}</span>
                </NavLink>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

/** Zeigt den Inhalt nur, wenn die Bedingung des Moduls gilt. Die Quelle ist ein Hook und bleibt je Eintrag dieselbe. */
function Bedingt({ quelle, children, sonst = null }: { quelle: () => boolean; children: ReactNode; sonst?: ReactNode }) {
  return quelle() ? children : sonst;
}

/** Eine Position der unteren Leiste: der erste Eintrag, dessen Bedingung gilt (Vorhaben vor Offen, wenn die Person in einem ist). */
function LeistenPlatz({ kandidaten, suche, ungelesen }: { kandidaten: readonly NavEintrag[]; suche: string; ungelesen: number }) {
  const [erster, ...rest] = kandidaten;
  if (!erster) return null;
  const link = <LeistenLink e={erster} suche={suche} ungelesen={ungelesen} />;
  const bedingung = erster.leisteBedingung ?? erster.bedingung;
  return bedingung ? (
    <Bedingt quelle={bedingung} sonst={<LeistenPlatz kandidaten={rest} suche={suche} ungelesen={ungelesen} />}>
      {link}
    </Bedingt>
  ) : (
    link
  );
}

function LeistenLink({ e, suche, ungelesen }: { e: NavEintrag; suche: string; ungelesen: number }) {
  if (e.dialog) {
    return (
      <Link to={dialogZiel(e.dialog, suche)} state={DIALOG_GEOEFFNET} aria-label={e.label}>
        <Icon name={e.icon} groesse={22} />
        {e.leisteLabel ?? e.label}
      </Link>
    );
  }
  // Mitteilungen: dieselbe Glocke wie in der Kopfzeile, am Handy führt sie zur Seite (KOPFZEILE.md, Abschnitt 4)
  if (e.pfad === MITTEILUNGEN_PFAD) {
    return (
      <NavLink to={e.pfad} aria-label={glockeText(ungelesen)}>
        <Glocke ungelesen={ungelesen} />
        {e.leisteLabel ?? e.label}
      </NavLink>
    );
  }
  return (
    <NavLink to={e.pfad} end={e.pfad === "/"}>
      <Icon name={e.icon} groesse={22} />
      {e.leisteLabel ?? e.label}
      {e.zaehler ? <Zaehler quelle={e.zaehler} klasse={s.leisteZahl} /> : null}
    </NavLink>
  );
}

/** Zähler eines Moduls neben dem Eintrag. Die Quelle ist ein Hook des Moduls und bleibt je Eintrag dieselbe. Orange nur, wenn das Modul
 *  ihn als dringend meldet (Meine Woche), sonst grau (BAUPLAN-SEITENLEISTE.md, Abschnitt 3). */
function Zaehler({ quelle, klasse }: { quelle: () => Zaehlerwert | null; klasse: string | undefined }) {
  const z = quelle();
  if (!z) return null;
  return (
    <span className={[klasse, z.dringend && s.dringend].filter(Boolean).join(" ")}>
      <span aria-hidden="true">{z.wert}</span>
      <span className="nur-leser">, {z.vorlesen}</span>
    </span>
  );
}
